import { useState, useEffect, useRef } from 'react';
import { fetchCached, resolveImageUrl } from '@/lib/api';
import SectionWatermark from '@/components/SectionWatermark';
import { FALLBACK_FOUNDER_CONTENT } from '@/lib/publicPageData';

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div ref={ref} className={className}
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: `all 0.7s ease-out ${delay}ms` }}>
      {children}
    </div>
  );
}

export default function FounderPage() {
  const [content, setContent] = useState(FALLBACK_FOUNDER_CONTENT);

  useEffect(() => {
    fetchCached('/content/founder').then(setContent).catch(() => {});
  }, []);

  return (
    <div data-testid="founder-page" className="pt-20 bg-white">
      {/* Hero */}
      <section className="mscm-hero-panel mscm-cinematic-hero py-20 md:py-28">
        <div className="mscm-hero-aurora" />
        <div className="mscm-star-field" />
        <div className="mscm-luminous-sweep" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <span className="text-white/85 text-xs font-bold uppercase tracking-[0.16em]">Million Stars Community Ministry</span>
          <h1 className="mscm-hero-title mt-4 font-bold text-white lg:text-6xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Our Founder
          </h1>
          <p className="mt-4 text-white/80 text-base md:text-lg">
            Meet the visionary behind Million Stars Community Ministry
          </p>
        </div>
      </section>

      {/* Founder Content */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">
            {/* Photo */}
            <AnimatedSection className="lg:col-span-2">
              <div className="sticky top-28">
                <div className="relative">
                  <img
                    data-testid="founder-photo"
                    src={resolveImageUrl(content.photo_url)}
                    alt={content.name}
                    className="w-full max-w-md mx-auto lg:mx-0 rounded-2xl shadow-2xl object-cover aspect-[3/4]"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute -bottom-4 right-0 w-24 h-24 bg-[#1E4FA1]/10 rounded-2xl -z-10 sm:-right-4" />
                </div>
                <div className="mt-8 text-center lg:text-left">
                  <h2
                    data-testid="founder-name"
                    className="text-2xl md:text-3xl font-bold text-[#0F172A]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {content.name}
                  </h2>
                  <p className="mt-1 text-[#1E4FA1] font-medium">{content.title}</p>
                </div>
              </div>
            </AnimatedSection>

            {/* Bio */}
            <AnimatedSection className="lg:col-span-3" delay={150}>
              <SectionWatermark
                as="div"
                contentClassName="relative"
                opacity={0.14}
                watermarkClassName="h-[min(100%,14rem)] w-[min(100%,14rem)] md:h-[min(100%,18rem)] md:w-[min(100%,18rem)] lg:h-[min(100%,20rem)] lg:w-[min(100%,20rem)]"
              >
                <div data-testid="founder-bio" className="prose prose-lg max-w-none">
                  {content.bio.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="text-[#475569] leading-relaxed mb-6 text-base">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </SectionWatermark>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
